import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Text } from 'src/@vymo/ui/atoms';
import List from 'src/@vymo/ui/blocks/list';
import Popup from 'src/@vymo/ui/blocks/popup';
import { ReactComponent as Dots } from 'src/assets/icons/dots.svg';
import { ReactComponent as Left } from 'src/assets/icons/left.svg';
import { ReactComponent as Right } from 'src/assets/icons/right.svg';
import { getVisiblePages } from './queries';
import { PaginationProps } from './types';
import styles from './index.module.scss';

function Pagination({
  currentPage = 1,
  pageSize = 10,
  totalPages,
  totalItems,
  setParentPageSize = _.noop,
  setParentPageIndex = _.noop,
  'data-test-id': dataTestId = 'pagination',
}: React.PropsWithChildren<PaginationProps>) {
  const [pageIndex, setPageIndex] = useState(currentPage);
  const [visiblePageIndex, setVisiblePageIndex] = useState(currentPage);
  const [visiblePages, setVisiblePages] = useState<number[]>([]);

  const options = useMemo(
    () =>
      [10, 20, 30, 40, 50].map((item) => ({
        label: `${item.toLocaleString()} / page`,
        value: item,
      })),
    [],
  );

  useEffect(() => {
    setPageIndex(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const pages = getVisiblePages(visiblePageIndex, totalPages, 3);
    setVisiblePages(pages);
  }, [visiblePageIndex, totalPages]);

  const onPageIndexChange = useCallback(
    (updatedPageIndex) => {
      const page = updatedPageIndex ? updatedPageIndex - 1 : 0;
      setParentPageIndex(page);
    },
    [setParentPageIndex],
  );

  const handleOnEllipseClick = useCallback(
    (dir) => {
      const pageJump = 3;
      if (dir === 'right') {
        const newIndex = Math.min(visiblePageIndex + pageJump, totalPages - 1);
        setVisiblePageIndex(newIndex);
      } else {
        const newIndex = Math.max(visiblePageIndex - pageJump, 2);
        setVisiblePageIndex(newIndex);
      }
    },
    [totalPages, visiblePageIndex],
  );

  const handlePageIndexClick = useCallback(
    (dir, index = -1) => {
      if (index > 0) {
        setPageIndex(index);
        setVisiblePageIndex(index);
        onPageIndexChange(index);
      } else {
        const newPageIndex = dir === 'right' ? pageIndex + 1 : pageIndex - 1;
        setPageIndex(newPageIndex);
        setVisiblePageIndex(newPageIndex);
        onPageIndexChange(newPageIndex);
      }
    },
    [onPageIndexChange, pageIndex],
  );

  const buttonType = useCallback(
    (curIndex) => {
      if (curIndex === Number(pageIndex)) {
        return 'primary';
      }
      return 'text';
    },
    [pageIndex],
  );
  const buttonVariant = useCallback(
    (curIndex) => {
      if (curIndex === Number(pageIndex)) {
        return 'subtle';
      }
      return 'default';
    },
    [pageIndex],
  );
  return (
    <div className={styles.pagination} data-test-id={dataTestId}>
      <div>
        {Number(totalItems) > 0 && (
          <div
            data-test-id={`${dataTestId}-page-count`}
            className={styles.pagination__pageCount}
          >
            Showing {(currentPage - 1) * pageSize + 1} -{' '}
            {Math.min(currentPage * pageSize, Number(totalItems))} of{' '}
            {totalItems} items
          </div>
        )}
      </div>
      <div>
        <Button
          type="text"
          onClick={() => handlePageIndexClick('left')}
          disabled={pageIndex === 1}
          iconProps={{ icon: <Left />, iconPosition: 'left' }}
          data-test-id="pagination-button-left"
        />
        <Button
          type={buttonType(1)}
          variant={buttonVariant(1)}
          onClick={() => handlePageIndexClick('', 1)}
          size="small"
          className={styles.pagination__btn}
        >
          {1}
        </Button>
        {Boolean(visiblePages && visiblePages[0] > 2) && (
          <Button
            type="text"
            onClick={() => handleOnEllipseClick('left')}
            iconProps={{ icon: <Dots />, iconPosition: 'left' }}
            className={styles.pagination__ellipsisBtn}
          />
        )}
        {visiblePages?.map((index) => (
          <Button
            key={`pagination-button-${index}`}
            onClick={() => handlePageIndexClick('', index)}
            type={buttonType(index)}
            variant={buttonVariant(index)}
            size="small"
            data-test-id={`pagination-button-${index}`}
            className={styles.pagination__btn}
          >
            {index}
          </Button>
        ))}
        {Boolean(
          visiblePages &&
            visiblePages[visiblePages.length - 1] < totalPages - 1,
        ) && (
          <Button
            type="text"
            onClick={() => handleOnEllipseClick('right')}
            iconProps={{ icon: <Dots />, iconPosition: 'left' }}
            className={styles.pagination__ellipsisBtn}
          />
        )}
        {totalPages > 1 && (
          <Button
            type={buttonType(totalPages)}
            variant={buttonVariant(totalPages)}
            onClick={() => handlePageIndexClick('', totalPages)}
            size="small"
            data-test-id={`pagination-button-${totalPages}`}
            className={styles.pagination__btn}
          >
            {totalPages}
          </Button>
        )}
        <Button
          type="text"
          onClick={() => handlePageIndexClick('right')}
          disabled={pageIndex === totalPages}
          iconProps={{ icon: <Right />, iconPosition: 'left' }}
          data-test-id="pagination-button-right"
        />

        <Popup
          content={
            <List
              onItemClick={(idx) => {
                const { value } = options[idx];
                setParentPageSize(Number(value));
                setPageIndex(1);
                setVisiblePageIndex(1);
              }}
              selectedIndex={
                options.findIndex((option) => option.value === pageSize) || 0
              }
            >
              {options.map((option) => (
                <List.Item
                  data-test-id={`pagination-select-page-size-optionList-${option.value}`}
                >
                  {option.label}
                </List.Item>
              ))}
            </List>
          }
          closeTrigger="click"
        >
          <div
            className={styles.pagination__pageSize}
            data-test-id="pagination-select-page-size"
          >
            <Text>{`${pageSize.toLocaleString()} / page`}</Text>
          </div>
        </Popup>
      </div>
    </div>
  );
}

export default Pagination;
